import React, { useEffect, useState } from "react";
import avatar1 from "../../assets/images/user1.png";
import { Modal } from "react-bootstrap";
import purchaseImg1 from "../../assets/images/p1.png";
import purchaseImg2 from "../../assets/images/p2.png";
import purchaseImg3 from "../../assets/images/p3.png";
import purchaseImg4 from "../../assets/images/p2.png";
import purchaseImg5 from "../../assets/images/p3.png";
import { SUBSCRIPTION, USER_MANAGEMENT } from "../../utils/const";
import useApi from "../../hook/useAPI";
import { toast } from "react-toastify";
import { isBillingLockedForPilot } from "../../constants/subscriptionStatus";
import { isAddonPlanName } from "../../utils/planCatalogDisplay";
import "./UserDetails.scss";

const OVERRIDE_TYPES = ['Pilot_Basic', 'Pilot_Full', 'Manual_Custom'];

const UserDetails = ({ show, handleClose, userId }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [planChanges, setPlanChanges] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [overrideType, setOverrideType] = useState('Pilot_Full');
  const [overrideAddonPlanIds, setOverrideAddonPlanIds] = useState([]);
  const [overrideExpiresAt, setOverrideExpiresAt] = useState('');
  const [overrideNote, setOverrideNote] = useState('');
  const [plans, setPlans] = useState([]);
  const [activeTab, setActiveTab] = useState('details');

  const { getData, putData, deleteData } = useApi();

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;

      try {
        const response = await getData(`${USER_MANAGEMENT.GET_SINGLE_USER}`, userId);
  
        if (response.status) {
          setUserDetails(response.users.user);
          setPlanChanges(response.users.planChanges || []);
          setActivePlan(response.users.activePlan || null);
          const override = response.users.activePlan?.override;
          if (override) {
            setOverrideType(
              OVERRIDE_TYPES.includes(override.overrideType)
                ? override.overrideType
                : 'Pilot_Basic'
            );
            setOverrideAddonPlanIds(Array.isArray(override.addonPlanIds) ? override.addonPlanIds : []);
            setOverrideExpiresAt(override.expiresAt ? new Date(override.expiresAt).toISOString().slice(0, 10) : '');
            setOverrideNote(override.note || '');
          }
        } else {
          console.log('Failed to fetch user details.');
        }
      } catch (err) {
        console.log('Error fetching user details:', err);
      }
    };

    const fetchPlans = async () => {
      try {
        const response = await getData(`${SUBSCRIPTION.GET_PRICE}`);
        setPlans(response?.data || []);
      } catch (err) {
        console.log('Error fetching plans:', err);
      }
    };

    if (show) {
      setActiveTab('details');
      fetchUserDetails();
      fetchPlans();
    }
  }, [userId, show]);

  useEffect(() => {
    if (overrideType !== 'Manual_Custom') {
      setOverrideAddonPlanIds([]);
    }
  }, [overrideType]);


  useEffect(() => {
    const fetchPlanChanges = async () => {
      if (!show || activeTab !== 'activity' || !userId) return;

      try {
        const response = await getData(
          `${USER_MANAGEMENT.PLAN_OVERRIDE}/${userId}/plan-changes?page=1&limit=50`,
          null,
          false
        );
        if (response?.status === 200) {
          setPlanChanges(response.data || []);
        }
      } catch (err) {
        console.log('Error fetching plan activity:', err);
      }
    };

    fetchPlanChanges();
  }, [activeTab, getData, show, userId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); 
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getPlanImage = (planName) => {
    switch (planName.toLowerCase()) {
      case 'all in one bundle':
        return purchaseImg1;
      case 'basic':
        return purchaseImg2;
      case 'trips':
        return purchaseImg3;
      case 'zones':
        return purchaseImg4;
      case 'reports':
        return purchaseImg5;
      default:
        return purchaseImg2;
    }
  };

  const refreshUserDetails = async () => {
    const response = await getData(`${USER_MANAGEMENT.GET_SINGLE_USER}`, userId);
    if (response?.status) {
      setUserDetails(response.users.user);
      setPlanChanges(response.users.planChanges || []);
      setActivePlan(response.users.activePlan || null);
    }
  };

  const getApiErrorMessage = (error, fallback) => (
    error?.response?.data?.message
    || error?.response?.data?.error
    || fallback
  );

  const handleSetOverride = async () => {
    if (managementLocked) {
      toast.error(managementLockMessage);
      return;
    }

    const normalizedOverrideType = OVERRIDE_TYPES.includes(overrideType)
      ? overrideType
      : 'Pilot_Basic';

    if (!overrideExpiresAt) {
      toast.error('Please select an override expiry date.');
      return;
    }

    const expiresAt = new Date(`${overrideExpiresAt}T23:59:59.999`);
    if (Number.isNaN(expiresAt.getTime()) || expiresAt <= new Date()) {
      toast.error('Override expiry must be in the future.');
      return;
    }

    if (normalizedOverrideType === 'Manual_Custom' && overrideAddonPlanIds.length === 0) {
      toast.error('Please select at least one add-on plan.');
      return;
    }

    const payload = {
      overrideType: normalizedOverrideType,
      expiresAt: expiresAt.toISOString(),
      note: overrideNote,
    };

    if (normalizedOverrideType === 'Manual_Custom') {
      payload.addonPlanIds = overrideAddonPlanIds;
    }

    try {
      const response = await putData(`${USER_MANAGEMENT.PLAN_OVERRIDE}/${userId}/plan-override`, payload);
      toast.success(response?.message || 'Plan override set successfully.');
      await refreshUserDetails();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to set plan override.'));
    }
  };

  const handleClearOverride = async () => {
    try {
      const response = await deleteData(`${USER_MANAGEMENT.PLAN_OVERRIDE}/${userId}/plan-override`);
      toast.success(response?.message || 'Plan override cleared successfully.');
      await refreshUserDetails();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to clear plan override.'));
    }
  };


  const billingLocked = isBillingLockedForPilot(activePlan);
  const managementLocked = billingLocked;
  const managementLockMessage = 'Plan override is disabled while the user has an active Stripe subscription (including past due or paused).';
  const overrideActive = activePlan?.override?.isActive;
  const getAddonDisplayName = (planName) => (planName === 'Report' ? 'Reports' : planName);
  const addonPlanSortKey = (plan) => {
    const key = (plan.planName || '').toLowerCase();
    const order = ['trips', 'zones', 'reports', 'report'];
    const index = order.indexOf(key);
    return index === -1 ? 999 : index;
  };
  const addonPlans = plans
    .filter((plan) => isAddonPlanName(plan))
    .sort((a, b) => addonPlanSortKey(a) - addonPlanSortKey(b));
  const handleAddonToggle = (planId) => {
    const normalizedId = String(planId);
    setOverrideAddonPlanIds((current) => {
      if (current.some((id) => String(id) === normalizedId)) {
        return current.filter((id) => String(id) !== normalizedId);
      }
      return [...current, normalizedId];
    });
  };
  const effectivePlanLabel = activePlan?.effective?.planName
    || activePlan?.billing?.planName
    || userDetails?.plan?.planName
    || 'None';

  const formatEventLabel = (event) => String(event || '').replace(/_/g, ' ');

  const accessBadges = [];
  if (activePlan?.trial?.isActive) {
    accessBadges.push({ key: 'trial', label: 'Trial', className: 'user-details-badge--trial' });
  }
  if (overrideActive) {
    const isAdminGranted = Boolean(activePlan?.override?.setByAdminId);
    accessBadges.push({
      key: 'pilot',
      label: isAdminGranted ? 'Admin custom plan' : 'Welcome access',
      className: 'user-details-badge--pilot',
    });
  }
  if (activePlan?.hasBillingSubscription) {
    accessBadges.push({
      key: 'billing',
      label: activePlan?.subscriptionStatus || 'Billing',
      className: 'user-details-badge--billing',
    });
  }
  if (accessBadges.length === 0) {
    accessBadges.push({ key: 'free', label: 'No subscription', className: 'user-details-badge--muted' });
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="xl"
      className="bs_common_modal add-device-modal user-details-modal"
    >
      <>
        <Modal.Header closeButton>
          <Modal.Title>User Detail</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="dialog-content-body ">
            <div className="user-profile-block">
              <div className="user-profile">
                <div className="user-profile-img">
                  <img src={avatar1} alt="User Avatar" />
                </div>
                <div className="user-profile-info">
                  <div className="user-name">{userDetails?.firstname} {userDetails?.lastname}</div>
                  <div className="user-email">{userDetails?.email}</div>
                </div>
              </div>
              <div className="user-plan-info">
                <div className="user-plan-status">Current access</div>
                <strong className="user-plan-name">{effectivePlanLabel}</strong>
                <div className="user-details-access-badges">
                  {accessBadges.map((badge) => (
                    <span key={badge.key} className={`user-details-badge ${badge.className}`}>
                      {badge.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="user-details-tabs">
              <button
                type="button"
                className={`user-details-tab ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>
              <button
                type="button"
                className={`user-details-tab ${activeTab === 'plan' ? 'active' : ''}`}
                onClick={() => setActiveTab('plan')}
              >
                Plan
              </button>
              <button
                type="button"
                className={`user-details-tab ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                Plan Activity
              </button>
            </div>

            {activeTab === 'details' && (
            <>
              <div className="user-details-info-grid">
                <div className="user-details-info-item">
                  <span>Gender</span>
                  <strong>{userDetails?.gender || '—'}</strong>
                </div>
                <div className="user-details-info-item">
                  <span>Phone number</span>
                  <strong>{userDetails?.phonenumber || '—'}</strong>
                </div>
                <div className="user-details-info-item">
                  <span>Date of birth</span>
                  <strong>{userDetails?.dateofbirth ? formatDate(userDetails?.dateofbirth) : '—'}</strong>
                </div>
                <div className="user-details-info-item">
                  <span>Account status</span>
                  <strong>{userDetails?.status || '—'}</strong>
                </div>
              </div>
            </>
            )}

            {activeTab === 'plan' && (
            <>
              {managementLocked && (
                <div className="plan-management-notice">{managementLockMessage}</div>
              )}
              <p className="user-details-plan-help">
                Use plan override for exceptional upgrades. Customers can still subscribe on their own account.
              </p>
              <section className="user-details-panel user-details-panel--full">
                <h4>Plan override</h4>
                <div className="user-details-field">
                  <label htmlFor="admin-override-type">Override type</label>
                  <select
                    id="admin-override-type"
                    className="form-select"
                    value={overrideType}
                    onChange={(e) => setOverrideType(e.target.value)}
                    disabled={managementLocked}
                  >
                    <option value="Pilot_Basic">Pilot Basic (Basic plan)</option>
                    <option value="Pilot_Full">Pilot Full (All-in-One Bundle)</option>
                    <option value="Manual_Custom">Manual Custom (Basic + Trips, Zones, Reports)</option>
                  </select>
                </div>
                {overrideType === 'Manual_Custom' && (
                  <div className="user-details-field">
                    <span>Add-on plans</span>
                    <div className="user-details-addon-list">
                      {addonPlans.map((plan) => (
                        <label key={plan._id}>
                          <input
                            type="checkbox"
                            checked={overrideAddonPlanIds.some((id) => String(id) === String(plan._id))}
                            onChange={() => handleAddonToggle(plan._id)}
                            disabled={managementLocked}
                          />
                          {getAddonDisplayName(plan.planName)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="user-details-field user-details-field--date">
                  <label htmlFor="admin-override-expires">Expires at</label>
                  <input
                    id="admin-override-expires"
                    type="date"
                    className="form-control"
                    value={overrideExpiresAt}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setOverrideExpiresAt(e.target.value)}
                    disabled={managementLocked}
                  />
                </div>
                <div className="user-details-field">
                  <label htmlFor="admin-override-note">Note</label>
                  <input
                    id="admin-override-note"
                    type="text"
                    className="form-control"
                    value={overrideNote}
                    onChange={(e) => setOverrideNote(e.target.value)}
                    disabled={managementLocked}
                    placeholder="Optional admin note"
                  />
                </div>
                <div className="user-details-panel-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSetOverride}
                    disabled={managementLocked || !overrideExpiresAt || (overrideType === 'Manual_Custom' && overrideAddonPlanIds.length === 0)}
                  >
                    Set override
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClearOverride}
                    disabled={!overrideActive}
                  >
                    Clear override
                  </button>
                </div>
              </section>
            </>
            )}

            {activeTab === 'activity' && (
              <div className="purchase-history-block">
                <h4>Plan activity</h4>
                {planChanges.length === 0 ? (
                  <p className="user-details-empty">No plan activity recorded yet.</p>
                ) : (
                  <div className="user-details-activity-list">
                    {planChanges.map((change) => (
                      <div key={change._id} className="user-details-activity-item">
                        <img
                          src={getPlanImage(change?.newEffectivePlanId?.planName || change?.newBillingPlanId?.planName || 'Basic')}
                          alt=""
                        />
                        <div className="user-details-activity-copy">
                          <strong>{formatEventLabel(change.event)}</strong>
                          <span>{new Date(change.createdAt).toLocaleString()} · Actor: {change.actor?.type || 'system'}</span>
                          <span>Billing: {change.newBillingPlanId?.planName || '—'} · Effective: {change.newEffectivePlanId?.planName || '—'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </Modal.Body>
      </>
    </Modal>
  );
};

export default UserDetails;
